import React, { useState } from "react";
import { Button, Dropdown, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { NavigateFunction, useNavigate } from "react-router-dom";
import {
  createColumnHelper,
  ColumnDef,
  Row,
  ExpandedState,
} from "@tanstack/react-table";
import { CourseCatalogSubTable } from "./CourseCatalogSubTable";
import { UnmappedSections } from "./UnmappedSections";
import { CoursesCatalogInterface, CourseStatus, CoursesCatalogStore, Override, SectionsCatalogInterface } from "Stores/courseStore";
import { FeatureGatingStore } from "Stores/featureGating";
import { inject, observer } from "mobx-react";
import { LateralDrawer } from "Components/Common/LateralDrawer";
import Form from "react-bootstrap/Form";
import { StyledModal } from "Components/Common/StyledModal";
import { IngestionStore } from "Stores/index";
import { FiltertIcon } from "Components/Icons/FilterIcon";
import { StyledTable } from "Components/Common/StyledTable";
import { OverrideComment } from "Components/Common/OverrideComment";
import { ClipboardListIcon } from "Components/Icons/ClipboardListIcon";
import { StyledChip } from "Components/Common/StyledChip";
import { reaction } from "mobx";
import { CaretDownIcon } from "Components/Icons/CaretDown";
import { CaretRightIcon } from "Components/Icons/CaretRight";
import { CheckCircleIcon } from "Components/Icons/CheckCircleIcon";
import { ArchivedIcon } from "Components/Icons/ArchivedIcon";
import { DisabledIcon } from "Components/Icons/DisabledIcon";
import { ProccesingIcon } from "Components/Icons/ProccesingIcon";
import { CreatableWrapper } from "Components/Common/SelectWrapper";
import { CurriculumMappingSubTable } from "./CurriculumMapping";
import { EllipsisVerticalIcon } from "Components/Icons/EllipsisVerticalIcon";
import { EditIcon } from "Components/Icons/EditIcon";
import { PlusIcon } from "Components/Icons/PlusIcon";
import {Utils} from "@/common/utils";
import { MagnifyingIcon } from "Components/Icons/MagnifyingIcon";
import PageTitle from "Components/Common/PageTitle";
import { CustomButton } from "Components/Common/CustomButton";
import { FileUpload } from "Components/Icons/FileUpload";
import ModalUpload from "./ModalUpload";
import { useFeatureGatingEnabled } from "@/hooks/feature-gating";
import { useToast } from "@/hooks/useToast";
export type CatalogInterface = "Courses" | "Sections";
export type openDropdownStatus = { open?: boolean, id?: string }
export type rowOptions = "edit" | "archive" | "disable" | "";

export interface EditCourseRequest {
  course_title: string;
  subject_number: string;
  curriculum_year: string;
  credit_hours: number;
  status: CourseStatus;
  last_term_id: number;
  cross_listed?: CoursesCatalogInterface[];
  update_comment: string | undefined;
  rid: number;
}

export interface CreateCourseRequest {
  course_title: string;
  subject_number: string;
  curriculum_year: string;
  credit_hours: number;
}

interface CurriculumYearOption {
  readonly label: string;
  readonly value: string;
}

export interface SectionsIds {
  sections: number[];
}
interface CourseCatalogState {
  catalog: CatalogInterface;
  inputTextSearch: string;
  showMoreFilters: boolean;
  initialLoading: boolean;
  initialUnmappedLoading: boolean;
  statusFilter: CourseStatus;
  curriculumFilter: string | undefined;
  overridenFilter: string | undefined;
  showCatalog: boolean
  courses: CoursesCatalogInterface[] | undefined;
  isOpenDrawerActions: rowOptions;
  isOpenDrawerCreate: boolean
  newCourse: CreateCourseRequest
  dropdownOpen: openDropdownStatus;
  isOverrideOpen: openDropdownStatus
  isCrossListedOpen: boolean;
  crossListedCourses: CoursesCatalogInterface[] | undefined;
  coursesToSelect: CoursesCatalogInterface[] | undefined;
  selectedCourse: CoursesCatalogInterface | undefined;
  statusFilterSection: boolean | undefined;
  isShowReason?: boolean;
  curriculumYearOptions: CurriculumYearOption[];
  selectedCurriculumYear: CurriculumYearOption | null;
  showImportCurriculumMapModal: boolean;
}

interface CoursesCatalogProps {
  coursesCatalogStore?: CoursesCatalogStore
  ingestionStore?: IngestionStore
  navigate: NavigateFunction
  featureGatingStore?: FeatureGatingStore;
  toast: (options: any) => void;
}

export function CourseCatalogPage() {
    const navigate = useNavigate();
    const { toast } = useToast();
    return <CourseCatalog navigate={navigate} toast={toast} />;
}

@inject("coursesCatalogStore", "ingestionStore", "featureGatingStore")
@observer
export class CourseCatalog extends React.Component<CoursesCatalogProps, CourseCatalogState> {

  constructor(props) {
    super(props);

    this.state = {
      dropdownOpen: {
        open: false,
        id: ""
      },
      isOverrideOpen: {
        id: "",
        open: false,
      },
      inputTextSearch: "",
      catalog: "Courses",
      showMoreFilters: false,
      initialLoading: true,
      initialUnmappedLoading: true,
      statusFilter: undefined,
      statusFilterSection: undefined,
      curriculumFilter: undefined,
      overridenFilter: undefined,
      showCatalog: true,
      courses: [],
      isOpenDrawerActions: "",
      isOpenDrawerCreate: false,
      isShowReason: false,
      newCourse: {
        course_title: "",
        credit_hours: 0,
        curriculum_year: "",
        subject_number: "",
      },
      isCrossListedOpen: false,
      crossListedCourses: [],
      coursesToSelect: [],
      selectedCourse: undefined,
      curriculumYearOptions: [],
      selectedCurriculumYear: null,
      showImportCurriculumMapModal: false
    };
    document.addEventListener("mousedown", this.handleDocumentClick);
  }

  navigateToCompetencyMapping = () => {
    const url = Utils.getLinkBase();
    this.props.navigate(url + "/competency-mapping");
  };

  searchOverride(override: Override, keyword: string): boolean {
    const searchProperty = (obj: Record<string, any>): boolean => {
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const value = obj[key];

          if (typeof value === "string" && value.toLowerCase().includes(keyword)) {
            return true;
          }
          if (typeof value === "object" && value !== null) {
            if (searchProperty(value)) {
              return true;
            }
          }
        }
      }

      return false;
    };
    return searchProperty(override);
  }

  searchCourses() {
    const { inputTextSearch, statusFilter, statusFilterSection, curriculumFilter, overridenFilter, showMoreFilters, catalog } = this.state;
    const { coursesCatalogStore } = this.props;

    if (!coursesCatalogStore) {
      return;
    }

    if (coursesCatalogStore?.coursesCatalog !== undefined) {
      let filteredCourses = [...coursesCatalogStore.coursesCatalog];
      let matchingCourses: CoursesCatalogInterface[] = [];
      if (catalog === "Courses") {
        if (inputTextSearch.length > 0) {
          const lowerKeyword = inputTextSearch.toLowerCase();
          filteredCourses = filteredCourses.filter((course) => {
            for (const key in course) {
              if (Object.prototype.hasOwnProperty.call(course, key)) {
                const value = course[key];
                if (typeof value === "string" && value.toLowerCase().includes(lowerKeyword)) {
                  return true;
                }
                if (key === "override" && this.searchOverride(value, lowerKeyword)) {
                  return true;
                }
              }
            }
            return false;
          });
        }
        if (showMoreFilters) {
          if (statusFilter) {
            filteredCourses = filteredCourses.filter((course: CoursesCatalogInterface) =>
              course.status === statusFilter.toLowerCase()
            );
          }

          if (curriculumFilter) {
            filteredCourses = filteredCourses.filter((course: CoursesCatalogInterface) => {
              if (course?.curriculum_year !== null && course?.curriculum_year !== undefined) {
                return course.curriculum_year.toLowerCase() === curriculumFilter.toLowerCase();
              }
            }
            );
          }

          if (overridenFilter) {
            filteredCourses = filteredCourses.filter((course: CoursesCatalogInterface) =>
              course.override?.changer?.name.toLowerCase() === overridenFilter.toLowerCase()
            );
          }
        }

        this.setState({ courses: filteredCourses });
      } else {

        if (inputTextSearch.length > 0) {
          const lowerKeyword = inputTextSearch.toLowerCase();
          coursesCatalogStore.coursesCatalog.forEach((course) => {
            const matchingSections = course.sections?.filter((section: SectionsCatalogInterface) => {
              for (const key in section) {
                if (Object.prototype.hasOwnProperty.call(section, key)) {
                  const value = section[key];
                  if (typeof value === "string" && value.toLowerCase().includes(lowerKeyword)) {
                    return true;
                  }
                }
              }
              return false;
            });

            if (matchingSections ? matchingSections.length > 0 : false) {
              matchingCourses.push({
                ...course,
                sections: matchingSections,
              });
            }
          });
        } else {
          matchingCourses = [...coursesCatalogStore.coursesCatalog];
        }

        if (showMoreFilters) {
          if (statusFilterSection !== undefined) {
            matchingCourses = matchingCourses.filter((course) =>
              course.sections?.some((section) => section.disabled === statusFilterSection)
            );
          }
          if (curriculumFilter) {
            matchingCourses = matchingCourses.filter((course: CoursesCatalogInterface) => {
              if (course?.curriculum_year !== null && course?.curriculum_year !== undefined) {
                return course.curriculum_year.toLowerCase() === curriculumFilter.toLowerCase();
              }
            }
            );
          }

          if (overridenFilter) {
            matchingCourses = matchingCourses.filter((course: CoursesCatalogInterface) =>
              course.override?.changer?.name.toLowerCase() === overridenFilter.toLowerCase()
            );
          }
        }
        this.setState({ courses: matchingCourses });
      }
    }
  }

  handleDocumentClick = (event) => {
    const dropdownButton = document.getElementById("dots-action");
    if (dropdownButton && !dropdownButton.contains(event.target)) {
      this.setState({ dropdownOpen: { id: "", open: false } });
    }
  };

  renderCourseCatalogStatus = (status: CourseStatus) => {

    if (status === "active") {
      return (
        <OverlayTrigger placement="top" overlay={<Tooltip style={{backgroundColor: "black", borderRadius: "5px",}}>
          Active
        </Tooltip>}>
            <CheckCircleIcon className="checkIcon" data-testid="check-icon" />
        </OverlayTrigger>
      );
    } else if (status === "archived") {
        return (
            <OverlayTrigger placement="top" overlay={<Tooltip style={{backgroundColor: "black", borderRadius: "5px",}}>Archived</Tooltip>}>
                <ArchivedIcon data-testid="archived-icon" />
            </OverlayTrigger>
        );
    } else if (status === "disabled") {
        return (
            <OverlayTrigger placement="top" overlay={<Tooltip style={{backgroundColor: "black", borderRadius: "5px",}}>Deactivated</Tooltip>}>
                <DisabledIcon data-testid="disable-icon" />
            </OverlayTrigger>
        );
    } else if (status === "processing") {
        return (
            <OverlayTrigger placement="top" overlay={<Tooltip style={{backgroundColor: "black", borderRadius: "5px",}}>Processing</Tooltip>}>
                <ProccesingIcon data-testid="processing-icon" />
            </OverlayTrigger>
        );
    }

    return status;
  };
  async componentDidMount() {
    if (!this.props.coursesCatalogStore || !this.props.ingestionStore) return;
    this.props.coursesCatalogStore.fetchCoursesCatalog();
    this.props.ingestionStore.fetchTerms();
    this.props.coursesCatalogStore?.fetchUnmappedSections(1, "");
    this.props.coursesCatalogStore?.fetchCognitiveLevels();
    this.props.coursesCatalogStore?.fetchCLOs();
    this.props.coursesCatalogStore?.fetchDomainCompetencies();
    this.props.coursesCatalogStore?.fetchTeachingMethods();
    reaction(() => this.props.coursesCatalogStore?.coursesCatalog, newCatalog => {
      if (newCatalog) {
        const newCurriculumYears = newCatalog.map((course) => course.curriculum_year);
        const newFilteredCurriculumYears = newCurriculumYears
          .filter((curriculum_year, index) => curriculum_year && curriculum_year.length > 0
            && newCurriculumYears.indexOf(curriculum_year) === index).sort().map((curriculum_year) => ({ label: curriculum_year, value: curriculum_year }));
        this.setState({ courses: newCatalog, initialLoading: false, curriculumYearOptions: newFilteredCurriculumYears });
        return;
      }

      this.setState({ courses: newCatalog, initialLoading: false });
    });
    reaction(() => this.props.coursesCatalogStore?.unmappedSections, newUnmapped => {
      this.setState({ initialUnmappedLoading: false });
    });
    Utils.setViewName("CourseCatalog", "Course Catalog");
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleDocumentClick);
  }
  onCloseEdit = () => {
    this.setState({ isOpenDrawerActions: "" });
  };

  onCloseCreate = () => {
    this.setState({ isOpenDrawerCreate: false });
  };

  onShowDrawerActions = (row: Row<CoursesCatalogInterface>, type: rowOptions) => {
    const defaultCurriculumYear = this.state.curriculumYearOptions.find((option) => option.value === row.original.curriculum_year);
    this.setState({ isOpenDrawerActions: type });
    this.setState(({
      selectedCourse: {
        course_title: row.original.course_title,
        credit_hours: row.original.credit_hours,
        cross_listed: row.original.cross_listed,
        last_term_id: row.original.last_term_id,
        curriculum_year: row.original.curriculum_year,
        status: row.original.status || "active",
        subject_number: row.original.subject_number,
        rid: row.original.rid,
        created: row.original.created,
        data_source: row.original.data_source,
        override: row.original.override,
        school_id: row.original.school_id,
        sections: row.original.sections,
        updated: row.original.updated,
        description: ""
      },
      isShowReason: false,
      selectedCurriculumYear: defaultCurriculumYear || null
    }));
  };

  onRefetchCourses = async () => {
    await this.props.coursesCatalogStore?.fetchCoursesCatalog();

    if (this.state.inputTextSearch) {
      this.searchCourses();
    }
  };

  onSaveEdit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { elements } = event.currentTarget;
    const courseTitle = elements.namedItem("course-title") as HTMLInputElement;
    const subjectNumber = elements.namedItem("subject-number") as HTMLInputElement;
    const curriculumYear = elements.namedItem("curriculum-year") as HTMLInputElement;
    const creditHours = elements.namedItem("credit-hours") as HTMLInputElement;
    const reasonChange = elements.namedItem("reason-change") as HTMLInputElement;
    const editObject = {
      course_title: courseTitle.value,
      subject_number: subjectNumber.value,
      curriculum_year: curriculumYear.value,
      credit_hours: parseFloat(creditHours.value),
      status: this.state.selectedCourse?.status,
      cross_listed: this.state.selectedCourse?.cross_listed,
      last_term_id: this.state.selectedCourse?.last_term_id as number,
      update_comment: reasonChange.value,
      rid: this.state.selectedCourse?.rid as number
    };
    await this.props.coursesCatalogStore?.updateCourseCatalog(editObject);
    this.setState({ isOpenDrawerActions: "" });
    await this.onRefetchCourses();
  };

  onSumbitDisabled = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const input = event.currentTarget.elements.namedItem("reason") as HTMLInputElement;
    const editObject: EditCourseRequest = {
      course_title: this.state.selectedCourse?.course_title || "",
      subject_number: this.state.selectedCourse?.subject_number || "",
      curriculum_year: this.state.selectedCourse?.curriculum_year || "",
      credit_hours: this.state.selectedCourse?.credit_hours || 0,
      status: this.state.selectedCourse?.status === "disabled" ? "active" : "disabled",
      cross_listed: this.state.selectedCourse?.cross_listed,
      last_term_id: this.state.selectedCourse?.last_term_id || 0,
      update_comment: input.value,
      rid: this.state.selectedCourse?.rid || 0
    };
    await this.props.coursesCatalogStore?.updateCourseCatalog(editObject);
    this.setState({ isOpenDrawerActions: "" });
    await this.onRefetchCourses();


  };

  onSubmitArchive = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const input = event.currentTarget.elements.namedItem("reason") as HTMLInputElement;
    const select = event.currentTarget.elements.namedItem("term") as HTMLSelectElement;
    if (this.state.selectedCourse?.status === "active" || this.state.selectedCourse?.status === "disabled") {
        await this.props.coursesCatalogStore?.archiveCouse(
          "archived",
          parseInt(select.value),
          input.value,
          String(this.state.selectedCourse?.rid));
      } else {
        const editObject: EditCourseRequest = {
          course_title: this.state.selectedCourse?.course_title || "",
          subject_number: this.state.selectedCourse?.subject_number || "",
          curriculum_year: this.state.selectedCourse?.curriculum_year || "",
          credit_hours: this.state.selectedCourse?.credit_hours || 0,
          status: "active",
          cross_listed: this.state.selectedCourse?.cross_listed,
          last_term_id: this.state.selectedCourse?.last_term_id || 0,
          update_comment: input.value,
          rid: this.state.selectedCourse?.rid || 0
        };
        await this.props.coursesCatalogStore?.updateCourseCatalog(editObject);
      }
    this.setState({ isOpenDrawerActions: "" });
    await this.onRefetchCourses();
  };

  onSubmitCrossed = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const course_id = this.state.selectedCourse?.rid;
    const related_courses = this.state.crossListedCourses?.map(course => course.rid) ?? [];
    await this.props.coursesCatalogStore?.crossCourses(course_id ?? 0, related_courses);
    this.setState({
      isCrossListedOpen: false
    });
    await this.onRefetchCourses();
  };

  renderFirstColWithIcon = (row: Row<CoursesCatalogInterface>) => {
    return (
      <div className="name-row">
        {row.getIsExpanded() ?
        (<CaretDownIcon onClick={row.getToggleExpandedHandler()} className={"icon-carets"} />) :
        (<CaretRightIcon onClick={row.getToggleExpandedHandler()} className={"icon-carets"} />)}
        {row.original.subject_number && row.original.subject_number.length > 25 ? (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${row.id}`} className="tooltip-st-status">{row.original.subject_number}</Tooltip>}
          >
            <span>{`${row.original.subject_number.substring(0, 25)}...`}</span>
          </OverlayTrigger>
        ) : (
          row.original.subject_number || "-"
        )}
      </div>
    );
  };

  renderTitle = (title: string) => {
    return (
      <div style={{ display: "flex", gap: 4 }}>
        {title}
      </div>
    );
  };

  openCrossListed = (row: Row<CoursesCatalogInterface>) => {
    const coursesFiltered = this.props.coursesCatalogStore?.coursesCatalog.filter(
      (course) => course.rid !== row.original.rid).filter(
        course => !row.original.cross_listed?.some(crossedCourse => crossedCourse.rid === course.rid));
    this.setState({
      isCrossListedOpen: true,
      selectedCourse: row.original,
      crossListedCourses: row.original.cross_listed,
      coursesToSelect: coursesFiltered
    });
  };

  renderCrossListedModal = () => {
    return (
      <StyledModal
        confirmButtonLabel="Save"
        cancelButtonLabel="Cancel"
        isOpen={this.state.isCrossListedOpen}
        onClose={() => this.setState({ isCrossListedOpen: false })}
        onSubmit={this.onSubmitCrossed}
        title="Crosslisted courses"
        subtitle="You can link or unlink this course to other courses"
        icon={<ClipboardListIcon />}
        disabledSubmit={this.state.crossListedCourses === this.state.selectedCourse?.cross_listed}
        isLoading={this.props.coursesCatalogStore?.isLoadingCrossed}
      >
        <Form.Group className="input-with-label" style={{ display: "flex", gap: 8 }}
          aria-hidden={!this.state.isCrossListedOpen ? "true" : "false"}>
          <Form.Select
            id="crosslisted"
            className="input-edit"
            name="crosslisted"
            onChange={(e) => {
              const courseToAdd: any = this.state.courses?.filter(
                (course: CoursesCatalogInterface) => String(course.rid) === e.target.value);
              this.setState({
                crossListedCourses: this.state.crossListedCourses?.concat(courseToAdd).sort(
                  (a, b) => {
                    return a.course_title.localeCompare(b.course_title);
                  }),
                coursesToSelect: this.state.coursesToSelect?.filter(
                  (course: CoursesCatalogInterface) => String(course.rid) !== e.target.value)
              });
            }}
          >
            <option value={""}>Select other course</option>
            {this.state.coursesToSelect?.map((course) => {
              return <option key={course.rid} value={course.rid}>{course.course_title}</option>;
            })}

          </Form.Select>
          <Form.Control.Feedback type="invalid">
            Please add or remove a course.
          </Form.Control.Feedback>
        </Form.Group>
        <div className="chips-container">
          {this.state.crossListedCourses?.map((course) => {
            return (
              <StyledChip key={course.rid} text={course.course_title} onClose={() => {
                const courseToRemove: any = this.state.courses?.filter(
                  (e: CoursesCatalogInterface) => e.rid === course.rid);
                this.setState({
                  crossListedCourses: this.state.crossListedCourses?.filter((e: CoursesCatalogInterface) => e.rid !== course.rid),
                  coursesToSelect: this.state.coursesToSelect?.concat(courseToRemove).sort(
                    (a, b) => {
                      return a.course_title.localeCompare(b.course_title);
                    })
                });
              }} />
            );
          })}
        </div>
      </StyledModal>
    );
  };

  renderDrawerEdit = () => {
    return (
      <LateralDrawer
        isOpen={this.state.isOpenDrawerActions === "edit"}
        onClose={this.onCloseEdit}
        title="Edit Course"
        confirmButton={{
          title: "Edit course",
          onClick: (e: React.FormEvent<HTMLFormElement>) => this.onSaveEdit(e),
          disabled: !this.state.isShowReason
        }}
        cancelButton={{
          title: "Cancel",
          onClick: this.onCloseEdit
        }}
      >
        <div className="drawer-form" data-testid="drawer-edit" aria-hidden={this.state.isOpenDrawerActions !== "edit" ? "true" : "false"}>
          <Form.Group className="input-with-label">
            <Form.Label className="label-input" htmlFor="course-title">
              Course Title
            </Form.Label>
            <Form.Control
              className="input-edit"
              type="text"
              required
              defaultValue={this.state.selectedCourse?.course_title}
              onChange={() => !this.state.isShowReason && this.setState({ isShowReason: true })}
              name="course-title"
              placeholder="Course title" />
            <Form.Control.Feedback type="invalid">
              Please provide a course title.
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="input-with-label">
            <Form.Label className="label-input" htmlFor="subject-number">
              Subject number
            </Form.Label>
            <Form.Control
              className="input-edit"
              type="text"
              required
              name="subject-number"
              onChange={() => !this.state.isShowReason && this.setState({ isShowReason: true })}
              defaultValue={this.state.selectedCourse?.subject_number}
              placeholder="Subject number" />
            <Form.Control.Feedback type="invalid">
              Please provide a subject number.
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="input-with-label">
            <Form.Label className="label-input" htmlFor="curriculum-year">
              Curriculum year
            </Form.Label>
            <CreatableWrapper
              name="curriculum-year"
              placeholder="Curriculum year"
              value={this.state.selectedCurriculumYear}
              onChange={(newValue) => {
                if (!this.state.isShowReason) {
                  this.setState({ isShowReason: true, selectedCurriculumYear: newValue });
                }
              }}
              required options={this.state.curriculumYearOptions} />
            <Form.Control.Feedback type="invalid">
              Please provide a curriculum year.
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="input-with-label">
            <Form.Label className="label-input" htmlFor="credit-hours">
              Credit hours
            </Form.Label>
            <Form.Control
              className="input-edit"
              type="number"
              required
              step="0.1"
              name="credit-hours"
              onChange={() => !this.state.isShowReason && this.setState({ isShowReason: true })}
              defaultValue={this.state.selectedCourse?.credit_hours}
              placeholder="Credit hours"
            />
            <Form.Control.Feedback type="invalid">
              Please provide a credit hour.
            </Form.Control.Feedback>
          </Form.Group>
          {this.state.isShowReason && <div className="divider" />}
          {this.state.isShowReason && <span className="reason-text">To continue, you need to share the reason for editing this course.</span>}
          {this.state.isShowReason &&
            <Form.Group>
              <Form.Control className="input-edit reason-input"
                type="text" as="textarea" name="reason-change" required rows={3} placeholder="Reason change" />
              <Form.Control.Feedback type="invalid">
                Please provide a reason.
              </Form.Control.Feedback>
            </Form.Group>
          }
        </div>
      </LateralDrawer >
    );
  };

  renderDrawerCreate = () => {

    const onCreateCourse = async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const { elements } = event.currentTarget;
      const courseTitle = elements.namedItem("course-title") as HTMLInputElement;
      const subjectNumber = elements.namedItem("subject-number") as HTMLInputElement;
      const curriculumYear = elements.namedItem("curriculum-year") as HTMLInputElement;
      const creditHours = elements.namedItem("credit-hours") as HTMLInputElement;
      let creditHoursValue: number;
      if (creditHours.value.includes(".")) {
        creditHoursValue = parseFloat(creditHours.value);
      } else {
        creditHoursValue = parseInt(creditHours.value);
      }
      const newCourse: CreateCourseRequest = {
        course_title: courseTitle.value,
        subject_number: subjectNumber.value,
        curriculum_year: curriculumYear.value,
        credit_hours: creditHoursValue,
      };
      await this.props.coursesCatalogStore?.createCourse(newCourse);
      this.props.toast({
          type: "success",
          title: "Course Created",
          message: `"${newCourse.course_title}" course was created successfully.`,
      });
      this.setState({ isOpenDrawerCreate: false });
      await this.props.coursesCatalogStore?.fetchCoursesCatalog();
      courseTitle.value = "";
      subjectNumber.value = "";
      curriculumYear.value = "";
      creditHours.value = "";
    };

    return (
      <LateralDrawer
        isOpen={this.state.isOpenDrawerCreate}
        onClose={this.onCloseCreate}
        title="Create Course"
        confirmButton={{
          title: "Create course",
          onClick: (e: React.FormEvent<HTMLFormElement>) => onCreateCourse(e),
        }}
        cancelButton={{
          title: "Cancel",
          onClick: this.onCloseCreate
        }}
      >
        <div className="drawer-form" data-testid="drawer-create" aria-hidden={!this.state.isOpenDrawerCreate ? "true" : "false"}>
          <Form.Group className="input-with-label">
            <Form.Label className="label-input" htmlFor="course-title">
              Course title<span className="text-danger">*</span>
            </Form.Label>

            <Form.Control
              className="input-edit"
              type="text"
              required
              name="course-title"
              defaultValue={""}
              placeholder="Course title"
            />
            <Form.Control.Feedback type="invalid">
              Please provide a course title.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="input-with-label">
            <Form.Label className="label-input" htmlFor="subject-number">
              Subject number<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              className="input-edit"
              type="text"
              required
              name="subject-number"
              placeholder="Subject number" />
            <Form.Control.Feedback type="invalid">
              Please provide a subject number.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="input-with-label">
            <Form.Label className="label-input" htmlFor="curriculum-year">
              Curriculum year<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              className="input-edit"
              type="text"
              required
              name="curriculum-year"
              placeholder="Curriculum year" />
            <Form.Control.Feedback type="invalid">
              Please provide a curriculum year.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="input-with-label">
            <Form.Label className="label-input" htmlFor="credit-hours">
              Credit hours
            </Form.Label>
            <Form.Control
              className="input-edit"
              type="number"
              step="0.1"
              name="credit-hours"
              placeholder="Credit hours" />
            <Form.Control.Feedback type="invalid">
              Please provide a credit hour.
            </Form.Control.Feedback>
          </Form.Group>
        </div>
      </LateralDrawer>
    );
  };

  renderDisabledModal = () => {
    return (
      <StyledModal
        isOpen={this.state.isOpenDrawerActions === "disable"}
        onClose={() => this.setState({ isOpenDrawerActions: "" })}
        title={`Are you sure you want to ${this.state.selectedCourse?.status === "disabled" ? "activate" : "deactivate"} this course?`}
        subtitle={`To continue, you need to share the reason for ${this.state.selectedCourse?.status === "disabled" ? "activating" : "deactivating"} this course`}
        cancelButtonLabel="Cancel"
        confirmButtonLabel={this.state.selectedCourse?.status === "disabled" ? "Activate" : "Deactivate"}
        onSubmit={this.onSumbitDisabled}
      >
        <Form.Group aria-hidden={this.state.isOpenDrawerActions !== "disable" ? "true" : "false"}>
          <label>Override comment
            <Form.Control
              as={"input"}
              name="reason"
              minLength={5}
              required
            />
          </label>
        </Form.Group>
      </StyledModal>
    );
  };

  renderArchivedModal = () => {
    const subtitle = this.state.selectedCourse?.status === "archived" ?
      "To continue, you need to select this course's last term and add the reason for retrieving it."
      : "Select this course's final term and add the reason for archiving it.";
    return (
      <StyledModal
        isOpen={this.state.isOpenDrawerActions === "archive"}
        onClose={() => this.setState({ isOpenDrawerActions: "" })}
        title={this.state.selectedCourse?.status === "archived" ? "Unarchive course" : "Archive course"}
        subtitle={subtitle}
        cancelButtonLabel="Cancel"
        confirmButtonLabel={this.state.selectedCourse?.status === "archived" ? "Unarchive" : "Archive"}
        onSubmit={this.onSubmitArchive}
      >
        {this.state.selectedCourse?.status !== "archived" && (<div>
          <Form.Select required name="term">
            <option value="">Choose Final Term</option>
            {this.props.ingestionStore?.terms.map((term) => {
              return (
                <option value={term.id} key={term.id}>{term.name}</option>
              );
            })}
          </Form.Select>
        </div>)}
        <div aria-hidden={this.state.isOpenDrawerActions !== "archive" ? "true" : "false"}>

          <Form.Group>
            <label>Override comment
              <Form.Control
                as={"input"}
                name="reason"
                minLength={5}
                required
              />
            </label>
          </Form.Group>
        </div>
      </StyledModal>
    );
  };


  columnHelper = createColumnHelper<CoursesCatalogInterface>();
  columns = [
    this.columnHelper.accessor("subject_number", {
      cell: ({ row }) => this.renderFirstColWithIcon(row),
      header: () => this.renderTitle("Subject Number")
    }),
    this.columnHelper.accessor("course_title", {
      cell: (value) => value.getValue() === null ? "-" : <div style={{textAlign: "left"}}>{value.getValue()}</div>,
      header: () => this.renderTitle("Course Title")
    }),
    this.columnHelper.accessor("status", {
      cell: (value) => this.renderCourseCatalogStatus(value.getValue()),
      header: () => this.renderTitle("Status")
    }),
    this.columnHelper.accessor("sections", {
      cell: (value) => value.row.original.sections?.length,
      header: () => this.renderTitle("Sections")
    }),
    this.columnHelper.accessor("curriculum_year", {
      cell: (value) => value.getValue() === null ? "-" : <div style={{textAlign: "center"}}>{value.getValue()}</div>,
      header: () => this.renderTitle("Curriculum Year")
    }),
    this.columnHelper.accessor("credit_hours", {
      cell: (value) => value.getValue() === null ? "-" : <div style={{textAlign: "center"}}>{value.getValue()}</div>,
      header: () => this.renderTitle("Credit Hours")
    }),
    this.columnHelper.accessor("override.changer.name", {
      cell: (value) => value.row.original.override?.changer === undefined ? "-" : <div style={{textAlign: "left"}}>{value.row.original.override?.changer.name}</div>,
      header: () => this.renderTitle("Last Edited By")
    }),
    this.columnHelper.accessor("override.comment", {
      cell: ({ row }) => <OverrideComment
        isOpen={String(row.original.rid) === this.state.isOverrideOpen.id}
        onClick={() => {
          this.setState({ isOverrideOpen: { id: this.state.isOverrideOpen.id ? "" : String(row.original.rid), open: !this.state.isOverrideOpen.open } });
        }}
        onClose={() => {
          this.setState({ isOverrideOpen: { id: "", open: false } });
        }}
        comment={row.original.override?.comment}
        date={row.original.override?.date ?? ""}
        username={row.original.override?.changer?.name}
        isDisabled={row.original.override?.changer === undefined}
      />,
      header: () => "Comment"
    }),
    this.columnHelper.display({
      id: "actions",
      cell: ({ row }) =>
        <div style={{ width: 40 }}>
          <MenuAction
            row={row}
            dropdownOpenId={String(this.state.dropdownOpen.id)}
            isOpen={Boolean(this.state.dropdownOpen.open)}
            onShowDrawer={(row, dropdownOpen) => this.onShowDrawerActions(row, dropdownOpen)}
            toogleShowMenu={(newState) => this.setState({ dropdownOpen: newState })}
          />
        </div>
    })
  ];

  renderSearchBar() {
    return (
      <div className="search-bar-container">
        <Dropdown className="courses-dropdown">
          <Dropdown.Toggle id="dropdown-basic" data-test-id="course-catalog-dropdown">
            <span className="option-selected">
              {this.state.catalog}
            </span>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item
              data-test-id="course-catalog-option-courses"
              onClick={() => this.setState({ catalog: "Courses", inputTextSearch: "" }, () => this.searchCourses())}
            >
              Courses
            </Dropdown.Item>
            <Dropdown.Item
              data-test-id="course-catalog-option-sections"
              onClick={() => this.setState({ catalog: "Sections", inputTextSearch: "" }, () => this.searchCourses())}
            >
              Sections
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
        <div className="divider" />
        <Form.Control
          value={this.state.inputTextSearch as string}
          onChange={(val) => {
            this.setState({ inputTextSearch: val.target.value.toString() }, () => {
              this.searchCourses();
            });
          }}
          type="text"
          as={"input"}
          placeholder="Search by Course title, subject number, curriculum year or credit hours"
        />
        <MagnifyingIcon />
      </div>
    );
  }
  renderTitleForFilter() {
    if (this.state.catalog === "Courses") {
      return this.state.statusFilter === undefined
        ? "All statuses"
        : this.state.statusFilter;
    } else {
      return this.state.statusFilterSection === undefined
        ? "All statuses"
        : this.state.statusFilterSection
          ? "Disable"
          : "Enable";
    }
  }
  renderAdvancedFilters() {
    return (
      <div className="advanced-filter-container" aria-hidden={!this.state.showMoreFilters ? "true" : "false"}>
        <Dropdown className="advaced-filter-dropdown" >
          <Dropdown.Toggle id="dropdown-basic">
            {this.renderTitleForFilter()}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {this.state.catalog === "Courses" ? (<>
              <Dropdown.Item
                onClick={() => this.setState({ statusFilter: undefined }, () => this.searchCourses())}
              >
                All statuses
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => this.setState({ statusFilter: "archived" }, () => this.searchCourses())}
              >
                Archived
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => this.setState({ statusFilter: "active" }, () => this.searchCourses())}
              >
                Active
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => this.setState({ statusFilter: "disabled" }, () => this.searchCourses())}
              >
                Deactivated
              </Dropdown.Item></>)
              : (<>
                <Dropdown.Item
                  onClick={() => this.setState({ statusFilterSection: undefined }, () => this.searchCourses())}
                >
                  Select status
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => this.setState({ statusFilterSection: false }, () => this.searchCourses())}
                >
                  Enabled
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => this.setState({ statusFilterSection: true }, () => this.searchCourses())}
                >
                  Disabled
                </Dropdown.Item>
              </>)}
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown className="advaced-filter-dropdown">
          <Dropdown.Toggle id="dropdown-basic">
            {this.state.curriculumFilter === undefined
              ? "All years"
              : this.state.curriculumFilter}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item
              key={"undefined"}
              onClick={() =>
                this.setState({ curriculumFilter: undefined }, () => this.searchCourses())
              }
            >
              All years
            </Dropdown.Item>
            {this.props.coursesCatalogStore?.coursesCatalog
              .map((course) => course.curriculum_year && course.curriculum_year)
              .filter((value, index, self) => self.indexOf(value) === index)
              .sort()
              .map((curriculumYear, index) => curriculumYear && (
                <Dropdown.Item
                  key={`${curriculumYear}-${index}`}
                  onClick={() =>
                    this.setState({ curriculumFilter: curriculumYear }, () => this.searchCourses())
                  }
                >
                  {curriculumYear}
                </Dropdown.Item>
              ))}
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown className="advaced-filter-dropdown">
          <Dropdown.Toggle id="dropdown-basic">
            {this.state.overridenFilter === undefined
              ? "All editors"
              : this.state.overridenFilter}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item
              key={"undefined"}
              onClick={() =>
                this.setState({ overridenFilter: undefined }, () => this.searchCourses())
              }
            >
              All editors
            </Dropdown.Item>
            {this.props.coursesCatalogStore?.coursesCatalog
              .map((course) => course.override?.changer?.name)
              .filter((value, index, self) => self.indexOf(value) === index)
              .map((changer, index) => (
                <Dropdown.Item
                  key={`${changer}-${index}`}
                  onClick={() =>
                    this.setState({ overridenFilter: changer }, () => this.searchCourses())
                  }
                >
                  {changer}
                </Dropdown.Item>
              ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  }
  render() {
    return (
      <>
        <PageTitle pageTitle="Courses Catalog" />
        <article className="main-course-catalog-container">
          {this.state.showCatalog ? (<>
            <div className="header-course-catalog">
              <h1 className="title-course-catalog">Course Catalog</h1>
              <div className="header-description-container">
                {(this.props.coursesCatalogStore?.unmappedSections ?
                  this.props.coursesCatalogStore.unmappedSections.data.total : 0) > 0 &&
                  <>
                    <div className="header-description">
                      <i className="icon icon-info-circle icon-yellow d-flex align-items-center justify-content-center" />
                      <p className="unmapped-warning">You have {this.props.coursesCatalogStore?.unmappedSections?.data?.total} unmapped sections you might want to check</p>
                    </div>
                    <span id="unmapped-sections-link" data-test-id="course-catalog-unmapped-sections" className="link-to-unmapped-sections" onClick={() => this.setState({ showCatalog: false })}>
                      See unmapped sections
                    </span>
                  </>
                }
                {
                  (Utils.isFeatureActive("ui", "competency_mapping", this.props.featureGatingStore!.featureGatingList)) &&
                    <>
                        <span id="competency-mapping-link" data-test-id="course-catalog-competency-mapping" className="link-to-unmapped-sections" onClick={() => this.navigateToCompetencyMapping()}>
                            Competency Catalog
                        </span>
                    </>
                }
                {
                    <>
                        <ImportCurriculumMapButton
                            onClick={() => this.setState({ showImportCurriculumMapModal: true })}
                        />
                       <ModalUpload
                            show={this.state.showImportCurriculumMapModal}
                            onClose={() => this.setState({ showImportCurriculumMapModal: false })}
                            onSuccess={() => {
                                this.props.coursesCatalogStore?.fetchCoursesCatalog();
                            }}
                        />
                    </>
                }
                <Button id="create-new-course-button" variant="primary" data-test-id="course-catalog-create-course" className="d-flex align-items-center new-course-btn gap-2 text-nowrap" onClick={() => this.setState({ isOpenDrawerCreate: true })}>
                    <PlusIcon className="white d-flex" />
                    Create new course
                </Button>
              </div>
            </div>
            <div className="search-filter-container">
              {this.renderSearchBar()}
              <div className="filter-container">
                <div
                  className="show-filters"
                  data-test-id="course-catalog-toggle-filters"
                  onClick={() =>
                    this.setState({ showMoreFilters: !this.state.showMoreFilters })
                  }
                >
                  <FiltertIcon />
                  <span className="filters-text">
                    {this.state.showMoreFilters ? "Hide Filters" : "Show filters"}
                  </span>
                </div>
              </div>
            </div>
            {this.state.showMoreFilters ? this.renderAdvancedFilters() : null}
            {this.state.initialLoading ? <div className="text-center"><Spinner variant="success" /></div> :
                <div className="responsive-table-wrapper">
                    <CourseCatalogTable
                        defaultData={this.state.courses || []}
                        columns={this.columns}
                        getRowCanExpand={(value) => Boolean(value.original.sections)}
                        coursesStore={this.props.coursesCatalogStore!}
                        featureGatingStore={this.props.featureGatingStore}
                        onRefetchCourses={this.onRefetchCourses}
                        toast={this.props.toast}
                    />
                </div>
            }
          </>)
            : (<UnmappedSections
              isLoading={this.state.initialUnmappedLoading || this.state.initialLoading}
              handleCreateCourse={() => this.setState({ isOpenDrawerCreate: true })}
              setShowCourseCatalog={() => this.setState({ showCatalog: true })}
              refreshCourseCatalog={this.onRefetchCourses}
            />)}
        </article>
        {this.renderDrawerEdit()}
        {this.renderDrawerCreate()}
        {this.renderDisabledModal()}
        {this.renderArchivedModal()}
        {this.renderCrossListedModal()}
      </>
    );
  }
}
type TableProps<CoursesCatalogInterface> = {
  defaultData: CoursesCatalogInterface[];
  columns: ColumnDef<CoursesCatalogInterface>[];
  getRowCanExpand: (row: Row<CoursesCatalogInterface>) => boolean;
  coursesStore: CoursesCatalogStore;
  featureGatingStore?: FeatureGatingStore;
  onRefetchCourses: () => Promise<void>;
  toast: (options: any) => void;
}
const CourseCatalogTable = observer(({ defaultData, columns, getRowCanExpand, coursesStore, featureGatingStore, onRefetchCourses, toast }: TableProps<CoursesCatalogInterface>) => {
  const [data, setData] = useState(() => [...defaultData]);
  const [openMapping, setOpenMapping] = useState(false);
  const [openSections, setOpenSections] = useState(false);
  const [editCurriculum, setEditCurriculum] = useState(false);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  React.useEffect(() => {
    if (data !== defaultData) {
      setData([...defaultData]);
    }
  }, [defaultData]);

  return (
    <div className="responsive-table-wrapper">
        <StyledTable
        defaultData={defaultData}
        columns={columns}
        getRowCanExpand={getRowCanExpand}
        autoResetPageIndex={false}
        expanded={expanded}
        setExpanded={setExpanded}
        onIsExpanded={(row) => {
            return (
            <div className="sub-sections-container">
                {
                    Utils.isFeatureActive("ui", "curriculum_mapping", featureGatingStore!.featureGatingList) &&
                    (
                        <>
                            <div className="sub-sections-option">
                            <div className="onclick-area" onClick={() => setOpenMapping(!openMapping)}>
                                {openMapping ?
                                <CaretDownIcon size="18" className="sub-section-caret" /> :
                                <CaretRightIcon size="18" className="sub-section-caret" />
                                }
                                Curriculum Mapping
                            </div>
                            <EditIcon
                                className="editIcon"
                                onClick={() => {
                                setOpenMapping(true);
                                setEditCurriculum(!editCurriculum);
                                }}
                            />
                            </div>
                            {openMapping &&
                                <CurriculumMappingSubTable
                                    initialData={row.original.curriculum_mapping}
                                    description={row.original.description ?? ""}
                                    editMode={editCurriculum}
                                    coursesCatalogStore={coursesStore}
                                    course_ruuid={row.original.rid}
                                    setEditMode={() => {
                                    setEditCurriculum(prevEditMode => !prevEditMode);
                                    }}
                                />
                            }
                        </>
                    )
                }
                <div className="sub-sections-option">
                    <div className={`onclick-area ${row.original.sections?.length === 0 ? "disabled-subarea" : ""}`}
                        onClick={row.original.sections?.length === 0 ?
                        () => null : () => setOpenSections(!openSections)
                    }>
                        {openSections ?
                        <CaretDownIcon size="20" className="sub-section-caret"/> :
                        <CaretRightIcon size="20" className="sub-section-caret"/>
                        }
                        Course Sections
                    </div>
                </div>
                {openSections && <CourseCatalogSubTable row={row.original} onCoursesRefetch={onRefetchCourses} toast={toast}/>}
            </div>
            );
        }}
        className="course-catalog-table"
        noDataMessage="No courses were found"
        />
    </div>);
});


interface MenuActionProps {
  row: Row<CoursesCatalogInterface>;
  onShowDrawer: (row: Row<CoursesCatalogInterface>, option: rowOptions) => void
  toogleShowMenu: (newState: openDropdownStatus) => void
  dropdownOpenId: string,
  isOpen: boolean
}
const MenuAction = ({ row, onShowDrawer, dropdownOpenId, isOpen, toogleShowMenu }: MenuActionProps) => {

  const renderMenu = (props) => {
    return (
      <Tooltip {...props} bsPrefix="course-catalog-tooltip">
        <div className="action-menu-container" aria-hidden={!isOpen}>
          <div className="action-menu-item" onClick={() => {
            onShowDrawer(row, "edit");
            toogleShowMenu({ id: "", open: false });
          }}>
            Edit
          </div>
          <div className="action-menu-item" onClick={async () => {
            onShowDrawer(row, "archive");
            toogleShowMenu({ id: "", open: false });
          }}>
            {row.original.status === "archived" ? "Unarchive" : "Archive"}
          </div>
          <div className="action-menu-item" onClick={() => {
            onShowDrawer(row, "disable");
            toogleShowMenu({ id: "", open: false });
          }}>
            {row.original.status === "disabled" ? "Activate" : "Deactivate"}
          </div>
        </div>
      </Tooltip>
    );
  };

  return (
    <div style={{ width: 40 }} >
      <OverlayTrigger rootClose trigger={"click"} overlay={renderMenu} show={dropdownOpenId === String(row.original.rid)} placement="top">
        <EllipsisVerticalIcon data-testid="dots-action" id="dots-action" className="threeDotsIcon"
          onClick={() => {
            toogleShowMenu({ id: dropdownOpenId === String(row.original.rid) ? "" : String(row.original.rid), open: isOpen ? false : true });
          }} />
      </OverlayTrigger >
    </div >
  );
};

function ImportCurriculumMapButton({onClick}: {onClick: () => void}) {
  const {enabled} = useFeatureGatingEnabled("ui", "curriculum_mapping");
  if (!enabled) {
    return null;
  }
  return (
      <CustomButton
          text="Import Curriculum Map"
          icon={<FileUpload />}
          onClick={onClick}
          variant="secondary"
      />
  );
}
